/**
 * Canonical analytics schemas.
 *
 * The analytics Lambda exposes 40+ endpoints (`/analytics/products`,
 * `/analytics/brands`, `/analytics/rfm`, …). Previously each endpoint
 * returned a bespoke shape; this module defines **one** canonical
 * `AnalyticsResponse` contract that every endpoint must satisfy.
 *
 * The frontend consumes the shape via typed hooks — no more heuristic
 * normalization (`api-data-normalizer`, `findRevenueKey`) is needed.
 */
import { z } from 'zod';
import { ISO8601Date, NonNegativeInt, PositiveInt, } from '../primitives/index.js';
import { Granularity, SortDirection, TrendDirection, } from '../enums/index.js';
/**
 * A single KPI card. Backend computes `previousValue`, `variation` and
 * `trend` when a `compareWith` window is provided in the request.
 */
export const KpiSchema = z.object({
    /** Stable identifier (e.g. "revenue", "orders", "aov"). */
    id: z.string().min(1),
    label: z.string().min(1),
    value: z.number().nullable(),
    previousValue: z.number().nullable().optional(),
    /** Signed ratio — `-0.15` means -15% vs previous period. */
    variation: z.number().nullable().optional(),
    format: z.enum(['currency', 'percentage', 'number']),
    trend: TrendDirection.optional(),
    /** Optional unit hint (e.g. "EUR", "min", "%"). */
    unit: z.string().optional(),
});
/** A single point on a time-series chart. `values` is measure → number. */
export const TrendPointSchema = z.object({
    /** Date string — typically YYYY-MM-DD, YYYY-Www, or YYYY-MM. */
    date: z.string(),
    values: z.record(z.string(), z.number().nullable()),
});
/** A single row of a breakdown (product row, brand row, abc_class row, …). */
export const BreakdownRowSchema = z.object({
    /** Human-readable dimension values (e.g. { product: "Widget", brand: "Acme" }). */
    dimensionValues: z.record(z.string(), z.string()),
    /** Aggregated measures for this row (e.g. { revenue: 1234.56, orders: 42 }). */
    measures: z.record(z.string(), z.number().nullable()),
    /** Optional 1-based rank within the breakdown (set when ordering applied). */
    rank: NonNegativeInt.optional(),
});
/**
 * A breakdown axis. An endpoint may include multiple breakdowns in a single
 * response (e.g. a dashboard with `by: product` + `by: brand` + `by: category`).
 */
export const BreakdownSchema = z.object({
    /** Dimension id on which rows are grouped. */
    by: z.string().min(1),
    /** Ordered list of dimension attribute names emitted per row. */
    dimensions: z.array(z.string().min(1)).min(1),
    /** Ordered list of measure names emitted per row. */
    measures: z.array(z.string().min(1)).min(1),
    rows: z.array(BreakdownRowSchema),
    /** Optional total row count before any `limit` was applied. */
    totalRows: NonNegativeInt.optional(),
});
/** Metadata attached to every response — useful for debugging and UIs. */
export const AnalyticsMetaSchema = z.object({
    factId: z.string(),
    period: z.object({ from: z.string(), to: z.string() }),
    compareWith: z
        .object({ from: ISO8601Date, to: ISO8601Date })
        .optional(),
    executionTimeMs: NonNegativeInt,
    rowCount: NonNegativeInt,
    /** Present when the server applied a warning or soft validation (e.g. fallback to dimension). */
    warnings: z.array(z.string()).optional(),
});
/** The canonical response envelope for every analytics endpoint. */
export const AnalyticsResponseSchema = z.object({
    kpis: z.array(KpiSchema),
    trend: z
        .object({
        granularity: Granularity,
        /** Measure names plotted on the trend chart. */
        measures: z.array(z.string().min(1)).min(1),
        points: z.array(TrendPointSchema),
    })
        .optional(),
    breakdowns: z.array(BreakdownSchema).optional(),
    meta: AnalyticsMetaSchema,
    /** Optional server message (e.g. "No data available yet."). */
    message: z.string().optional(),
    /** Filter options for dropdowns (e.g. available product brands). */
    filterOptions: z.array(z.string()).optional(),
});
/** Filter value for a query request — single string or list of strings. */
export const QueryFilterValueSchema = z.union([
    z.string(),
    z.array(z.string()).min(1),
]);
/** The canonical request body for the generic `POST /analytics/query` endpoint. */
export const QueryRequestSchema = z.object({
    factId: z.string().min(1),
    measures: z.array(z.string().min(1)).min(1),
    groupBy: z.array(z.string().min(1)).optional(),
    filters: z.record(z.string(), QueryFilterValueSchema).optional(),
    dateRange: z.object({ from: ISO8601Date, to: ISO8601Date }),
    compareWith: z
        .object({ from: ISO8601Date, to: ISO8601Date })
        .optional(),
    granularity: Granularity.optional(),
    orderBy: z.string().optional(),
    orderDir: SortDirection.optional(),
    limit: PositiveInt.max(10000).optional(),
});
/** Request query params shared by most analytics endpoints (GET style). */
export const AnalyticsQueryParamsSchema = z.object({
    from: ISO8601Date,
    to: ISO8601Date,
    compareFrom: ISO8601Date.optional(),
    compareTo: ISO8601Date.optional(),
    granularity: Granularity.optional(),
    limit: PositiveInt.max(10000).optional(),
    /** Comma-separated list of dimension filter expressions, parsed server-side. */
    filter: z.string().optional(),
});
/**
 * `GET /analytics/model` success body — the analytics handler's odd one out.
 * Unlike the 23 fact endpoints that share AnalyticsResponseSchema, `/model`
 * returns the raw DataModel document (versioned semantic layer).
 *
 * Shape is intentionally loose — DataModel documents carry many per-vertical
 * fields and the schema lives in entities/data-model for full validation.
 */
export const AnalyticsModelResponseSchema = z.object({
    dataModel: z.looseObject({
        version: z.number().int().nonnegative(),
        facts: z.record(z.string(), z.unknown()),
        dimensions: z.record(z.string(), z.unknown()),
        features: z.record(z.string(), z.unknown()),
        updatedAt: z.string(),
        templateId: z.string().nullable().optional(),
    }),
});
// ============================================================================
// NON-CANONICAL ANALYTICS ENDPOINTS
// ============================================================================
// A handful of analytics endpoints don't fit the canonical
// `{ kpis, trend, breakdowns, meta }` envelope. They are lookup / search /
// drill-down style endpoints whose responses are intentionally shaped around
// their consumers (sidebar dropdowns, header search bar, drill-down pages).
// Each has its own explicit schema below.
/**
 * `GET /analytics/channels` / `/warehouses` / `/suppliers` success body.
 *
 * These endpoints populate the sidebar filter dropdowns. They return a bare
 * array of dimension rows — schema is loose because the exact columns vary
 * per tenant DataModel (e.g. channels expose `channel_id|name|type`,
 * warehouses expose `warehouse_key|name|city`, suppliers expose
 * `supplier_key|name|city|country_code`).
 */
export const AnalyticsListResponseSchema = z.array(z.looseObject({}));
/** A single grouped search hit in a global-search response. */
export const GlobalSearchItemSchema = z.looseObject({
    id: z.string().optional(),
    name: z.string(),
    secondary: z.string().optional(),
});
/**
 * `GET /analytics/global-search?q=...` success body.
 * Used by the header search bar — groups hits by dimension type.
 */
export const AnalyticsGlobalSearchResponseSchema = z.object({
    query: z.string(),
    results: z.record(z.string(), z.object({
        label: z.string(),
        items: z.array(GlobalSearchItemSchema),
    })),
    totalCount: NonNegativeInt,
});
/**
 * `POST /analytics/query` success body — the generic dynamic-query endpoint.
 *
 * Unlike the fact endpoints, this returns a raw row-based envelope:
 * `{ fact, measures, dimensions, filters, date_range, rows, data }`.
 * Rows are left as `unknown` because their shape depends on the requested
 * measures + dimensions; the consumer is expected to project them.
 */
export const AnalyticsQueryResponseSchema = z.looseObject({
    fact: z.string(),
    // measures/dimensions come from buildQuery metadata on the happy path but
    // are omitted on error-fallback paths (e.g. ER_NO_SUCH_TABLE, missing
    // DataModel) which return `{ fact, rows: 0, data: [], message }`.
    measures: z.array(z.unknown()).optional(),
    dimensions: z.array(z.unknown()).optional(),
    filters: z.record(z.string(), z.unknown()).optional(),
    date_range: z.unknown().optional(),
    rows: NonNegativeInt,
    data: z.array(z.looseObject({})),
    message: z.string().optional(),
});
/**
 * `GET /analytics/detail?type=...&value=...` success body.
 *
 * Drill-down endpoint with 6+ resource types (brand, category, product,
 * geography, supplier, customer); each returns a different bespoke payload
 * (kpis, trend points, top products, ABC analysis, …). Shape is intentionally
 * loose — validation is per-type at the UI boundary. Early-return error paths
 * (invalid type, missing value) return `{ message }`, which this schema also
 * accepts.
 */
export const AnalyticsResourceDetailResponseSchema = z.looseObject({
    message: z.string().optional(),
});
//# sourceMappingURL=index.js.map